import { init as SentryInitCapacitor } from '@sentry/capacitor';
import {
	type Breadcrumb,
	type BrowserOptions,
	captureException as SentryCaptureException,
	createReduxEnhancer as SentryCreateReduxEnhancer,
	init as SentryInitReact,
	type SeverityLevel,
	addBreadcrumb,
	getCurrentScope,
} from '@sentry/react';
import { pathOr } from 'lodash/fp';
import type { Middleware } from 'redux';

import { Locale } from '@skippay/core/@types/country';
import { overAnd } from '@skippay/core/utils/fp';

export { captureMessage, setTag } from '@sentry/react';

export function captureException(
	error: Error,
	captureContext: Parameters<typeof SentryCaptureException>[1]
): ReturnType<typeof SentryCaptureException> {
	if (process.env.TARGET_ENV !== 'prod') {
		// eslint-disable-next-line no-console
		console.error('❌ Error captured:', error);
	}

	return SentryCaptureException(error, captureContext);
}

export type SanitizeReduxActions = Exclude<
	Required<Parameters<typeof SentryCreateReduxEnhancer>>[0]['actionTransformer'],
	undefined
>;
export type SanitizeReduxState = Exclude<
	Required<Parameters<typeof SentryCreateReduxEnhancer>>[0]['stateTransformer'],
	undefined
>;
export type SanitizeSentryEvent = Exclude<BrowserOptions['beforeSend'], undefined>;

const userIdSelector = pathOr(`NOT_LOGGED_IN`, [`auth`, `userId`]);

export enum BreadcrumbCategory {
	auth = 'auth',
	buttonClick = 'buttonClick',
	gate = 'gate',
	linkClick = 'linkClick',
	revolving = 'revolving',
	firebaseDatabase = 'firebaseDatabase',
	installmentApplication = 'installmentApplication',
	insurance = 'insurance',
	limitAmendment = 'limitAmendment',
	billingPayment = 'billingPayment',
}

export const setLocale = (locale: Locale) => {
	getCurrentScope().setTag('locale', locale);
};

export const captureBreadcrumb = (
	category: BreadcrumbCategory,
	message: Breadcrumb['message'],
	data?: Breadcrumb['data'],
	level: SeverityLevel = 'info'
): void => {
	addBreadcrumb({
		category,
		message,
		data,
		level,
	});

	if (__DEV__) {
		// eslint-disable-next-line no-console
		console.log(`[${level}] <${category}> ${message}`, data);
	}
};

/**
 * This is used to track only sample of GTM errors. GTM errors are not critical and if occurs, are tracked in huge
 * amounts which is costly.
 * @param {number} sampleRate between 0-1 as percentage of GTM events to tracks
 */
const sampleGTMErrors = (sampleRate: number) => (event, hint) => {
	try {
		const isGTM = hint.originalException.stack.includes(`www.googletagmanager.com/gtm.js?id=`);
		if (isGTM && (sampleRate === 0 || Math.random() > sampleRate)) {
			return null;
		}
		// eslint-disable-next-line no-empty
	} catch (error) {}
	return event;
};

// https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/#decluttering-sentry
const getIgnoredErrorsAndUrlsList = () => {
	return {
		errors: [
			'top.GLOBALS',
			'originalCreateNotification',
			'canvas.contentDocument',
			'MyApp_RemoveAllHighlights',
			'http://tt.epicplay.com',
			"Can't find variable: ZiteReader",
			'jigsaw is not defined',
			'ComboSearch is not defined',
			'http://loading.retry.widdit.com/',
			'atomicFindClose',
			'fb_xd_fragment',
			'bmi_SafeAddOnload',
			'EBCallBackMessageReceived',
			'conduitPage',
			'Non-Error exception captured',
			'Script error.',
			'_avast_submit',
			'Network Error', // LinkedIn tracking error
			'pintrk is not defined', // is part of Pinterest's tracking script
			"Can't find variable: pintrk", // is part of Pinterest's tracking script
			'pintrk is not defined', // is part of Pinterest's tracking script
			"Can't find variable: gmo", // related to https://issuetracker.google.com/issues/396043331?pli=1
			'Load failed', // google analytics¨
			'Failed to fetch', // Sentry core
			'Request failed with status code 404', // Gatsby error
		],
		urls: [
			/pagead\/js/i,
			/graph\.facebook\.com/i,
			/connect\.facebook\.net\/en_US\/all\.js/i,
			/eatdifferent\.com\.woopra-ns\.com/i,
			/static\.woopra\.com\/js\/woopra\.js/i,
			/extensions\//i,
			/^chrome:\/\//i,
			/^chrome-extension:\/\//i,
			/127\.0\.0\.1:4001\/isrunning/i,
			/webappstoolbarba\.texthelp\.com\//i,
			/metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
			/clarity\.ms/i,
			/webappstoolbarba\.texthelp\.com\//i,
			/metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
		],
	};
};

/**
 * Initialises Sentry error tracking and returns Sentry redux middleware.
 * @param {Object} options - error tracking options
 * @param {boolean} [options.debug] if set, captured errors will be printed to the console and not sent to the tracker.
 * 	For use in local development mode.
 * @param {string} options.dsn Sentry's Data Source Name
 * @see https://docs.sentry.io/product/sentry-basics/dsn-explainer/
 * @param {string} [options.environment=development] current lifecycle environment. If process.env.NODE_ENV is set to
 * 	`development`, this option is forced to `development` and cannot be configured.
 * @see https://docs.sentry.io/product/sentry-basics/environments/
 * @param {string} [options.release] used to track releases in Sentry
 * @see https://docs.sentry.io/product/releases/
 * @param {Object} [options.sanitizers] - data sanitizers to prevent privacy leak
 * @return {Middleware} Sentry redux middleware. Tying init with redux middleware creation is not necessary but enforces
 * 	calling the init before creation of redux store.
 */
const createDebugFunction = () => {
	return (event, hint) => {
		// eslint-disable-next-line no-console
		console.error(
			`In production will be tracked to Sentry:\n`,
			hint?.originalException || hint?.syntheticException || event
		);
		// returning null prevents sending of the event
		return null;
	};
};

export const init = ({
	mobile = false,
	debug,
	dsn,
	environment = `development`,
	release,
	sanitizers,
}: {
	mobile: boolean;
	debug?: boolean;
	dsn: string;
	environment?: string;
	release?: string;
	sanitizers?: {
		sanitizeReduxActions?: SanitizeReduxActions;
		sanitizeReduxState?: SanitizeReduxState;
		sanitizeSentryEvent?: SanitizeSentryEvent;
	};
}): Middleware => {
	const ignoreList = getIgnoredErrorsAndUrlsList();
	const debugFunction = createDebugFunction();

	if (mobile) {
		// using CapacitorOptions
		SentryInitCapacitor(
			{
				dsn,
				// force environment `development` in local development to prevent accidental pollution of sentry statistics
				environment: process.env.NODE_ENV === `development` ? `development` : environment,
				beforeSend: overAnd(
					[
						debug ? debugFunction : null,
						sampleGTMErrors(0.15),
						sanitizers?.sanitizeSentryEvent,
					].filter(Boolean)
				),
				ignoreErrors: ignoreList.errors,
				denyUrls: ignoreList.urls,
				normalizeDepth: 10,
				release,
				tracesSampleRate: 0.1,
			},
			SentryInitReact
		);
	} else {
		// using BrowserOptions
		SentryInitReact({
			dsn,
			// force environment `development` in local development to prevent accidental pollution of sentry statistics
			environment: process.env.NODE_ENV === `development` ? `development` : environment,
			beforeSend: overAnd(
				[
					debug ? debugFunction : null,
					sampleGTMErrors(0.15),
					sanitizers?.sanitizeSentryEvent,
				].filter(Boolean)
			),
			ignoreErrors: ignoreList.errors,
			denyUrls: ignoreList.urls,
			normalizeDepth: 10,
			release,
			tracesSampleRate: 0.1,
		});
	}

	return SentryCreateReduxEnhancer({
		actionTransformer: sanitizers?.sanitizeReduxActions,
		configureScopeWithState: (scope, state) => {
			scope.setUser({ userId: userIdSelector(state) });
			scope.setExtra('appVersion', process.env.npm_package_version);
		},
		stateTransformer: sanitizers?.sanitizeReduxState,
	});
};
